import { useState, useEffect, useRef } from 'react';
import content from '../content.json';
const pub = process.env.PUBLIC_URL;
const p = content.project;

const HEIGHTS = [280,350,210,320,400,250,300,240];

function placeholderSrc(i) {
  const bg = ['#2a2318','#221c14','#261e16','#1e1810','#281f15','#201a12'][i%6];
  const svg = `<svg xmlns='http://www.w3.org/2000/svg' width='600' height='400'><rect width='600' height='400' fill='${bg}'/><text x='300' y='200' text-anchor='middle' font-family='serif' font-size='11' fill='rgba(200,169,110,0.2)' letter-spacing='3'>FOTO ${i+1}</text></svg>`;
  return `data:image/svg+xml;utf8,${encodeURIComponent(svg)}`;
}


function useCountUp(target, duration = 1800, start = false) {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!start) return;
    let startTime = null;
    const step = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      // easeOutQuart
      const eased = 1 - Math.pow(1 - progress, 4);
      setCount(Math.floor(eased * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [start, target, duration]);
  return count;
}

function CounterRow() {
  const ref = useRef(null);
  const [started, setStarted] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setStarted(true); observer.disconnect(); } },
      { threshold: 0.4 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const c1 = useCountUp(10, 1600, started);
  const c2 = useCountUp(3, 1000, started);
  const c3 = useCountUp(500, 2000, started);

  const counters = [
    { value: c1, suffix: '+', label: 'Years of Restoration' },
    { value: c2, suffix: '', label: 'Studio Rooms' },
    { value: c3, prefix: '>', suffix: '', label: 'Hosted Artworks' },
  ];

  return (
    <div ref={ref} style={{ display:'flex',gap:'80px',alignItems:'flex-start',justifyContent:'center',marginBottom:'48px' }}>
      {counters.map((item, i) => (
        <div key={i} style={{ textAlign:'center' }}>
          <div style={{ fontFamily:'var(--font-title)',fontWeight:400,fontSize:'clamp(2.5rem,5vw,4.5rem)',color:'white',lineHeight:1,marginBottom:'10px' }}>
            {item.prefix || ''}{item.value}{item.suffix}
          </div>
          <div style={{ fontFamily:'var(--font-body)',fontSize:'11px',letterSpacing:'0.15em',color:'rgba(255,255,255,0.65)',textTransform:'uppercase' }}>{item.label}</div>
        </div>
      ))}
    </div>
  );
}

export default function ProjectPage() {
  const [hovered, setHovered] = useState(null);

  const photos = p.masonry.length > 0 ? p.masonry : Array(8).fill(null);

  return (
    <div style={{ background: '#ffffff', minHeight: '100vh', paddingTop: 'var(--nav-height)' }}>

      {/* Hero — single correct image */}
      <div style={{ position:'relative',height:'65vh',overflow:'hidden' }}>
        <img src={`${pub}/${p.heroImage}`} alt="The Project" style={{ width:'100%',height:'100%',objectFit:'cover' }} />
        <div style={{ position:'absolute',inset:0,background:'linear-gradient(to bottom,rgba(14,12,10,0.15),rgba(14,12,10,0.55))' }} />
        <div style={{ position:'absolute',inset:0,display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',textAlign:'center',padding:'0 48px' }}>
          <span style={{ fontFamily:'var(--font-body)',fontSize:'11px',letterSpacing:'0.32em',textTransform:'uppercase',color:'var(--gold2)',display:'block',marginBottom:'16px' }}>Discover Our Story</span>
          <h1 style={{ fontFamily:'var(--font-title)',fontWeight:400,fontStyle:'normal',fontSize:'clamp(2rem,5vw,3.8rem)',color:'white',lineHeight:1.15 }}>The Project</h1>
        </div>
      </div>

      {/* Embark on the Journey — single image */}
      <div style={{ maxWidth:'1160px',margin:'0 auto',padding:'100px 48px' }}>
        <div style={{ display:'grid',gridTemplateColumns:'1fr 1fr',gap:'80px',alignItems:'start' }}>
          <div>
            <span className="section-label reveal">{p.label}</span>
            <h2 className="section-title reveal delay-1">{p.title}</h2>
            <div className="gold-line reveal delay-2" />
            {p.text.split('\n\n').map((para, i) => (
              <p key={i} className="reveal" style={{ transitionDelay:`${(i+2)*0.08}s`,fontFamily:'var(--font-body)',fontSize:'14px',lineHeight:1.9,color:'var(--text2)',marginBottom:'20px' }}>{para}</p>
            ))}
          </div>
          {/* Single embark image */}
          <div className="reveal delay-1" style={{ position:'sticky',top:'100px',overflow:'hidden' }}>
            <img src={`${pub}/${p.embarkImage}`} alt="Embark" style={{ width:'100%',height:'520px',objectFit:'cover',display:'block',transition:'transform 0.6s ease' }}
              onMouseEnter={e=>e.target.style.transform='scale(1.03)'}
              onMouseLeave={e=>e.target.style.transform='scale(1)'}
            />
          </div>
        </div>
      </div>

      {/* Masonry */}
      <div style={{ background:'var(--dark)',padding:'80px 0' }}>
        <div style={{ maxWidth:'1160px',margin:'0 auto',padding:'0 48px 48px',textAlign:'center' }}>
          <span className="section-label reveal" style={{ color:'var(--gold2)' }}>The Space</span>
          <h2 className="section-title reveal delay-1" style={{ color:'var(--offwhite)' }}>Explore <em style={{ color:'var(--gold2)' }}>Silverkammer</em></h2>
          <div className="gold-line center reveal delay-2" />
          {p.masonry.length === 0 && <p style={{ fontFamily:'var(--font-body)',fontSize:'12px',color:'rgba(248,245,240,0.3)',letterSpacing:'0.1em',marginTop:'8px' }}>Photos coming soon</p>}
        </div>
        <div className="reveal" style={{ columnCount:3,columnGap:'4px',padding:'0 4px',maxWidth:'1600px',margin:'0 auto' }}>
          {photos.map((photo, i) => {
            const hasImg = photo && photo.trim() !== '';
            const caption = p.masonryCaptions && p.masonryCaptions[i] ? p.masonryCaptions[i] : '';
            const src = hasImg ? `${pub}/${photo}` : placeholderSrc(i);
            return (
              <div key={i} style={{ breakInside:'avoid',marginBottom:'4px',position:'relative',overflow:'hidden' }}
                onMouseEnter={()=>setHovered(i)} onMouseLeave={()=>setHovered(null)}>
                <img src={src} alt={caption} style={{ width:'100%',display:'block',height:hasImg?'auto':`${HEIGHTS[i%HEIGHTS.length]}px`,objectFit:'cover',transition:'transform 0.6s ease',transform:hovered===i?'scale(1.05)':'scale(1)' }} />
                {caption && (
                  <div style={{ position:'absolute',inset:0,background:hovered===i?'linear-gradient(to top,rgba(200,150,60,0.85) 0%,transparent 55%)':'transparent',transition:'background 0.4s',display:'flex',alignItems:'flex-end',padding:'16px 14px' }}>
                    <p style={{ fontFamily:'var(--font-body)',fontSize:'11px',fontWeight:700,letterSpacing:'0.08em',textTransform:'uppercase',color:'white',opacity:hovered===i?1:0,transform:hovered===i?'translateY(0)':'translateY(6px)',transition:'all 0.4s' }}>{caption}</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Meet the Faces */}
      <div style={{ background:'var(--offwhite)',padding:'100px 0',borderTop:'1px solid #ece8e0' }}>
        <div style={{ maxWidth:'960px',margin:'0 auto',padding:'0 48px',textAlign:'center' }}>
          <span className="section-label reveal">The team</span>
          <h2 className="section-title reveal delay-1">Meet the <em>faces</em></h2>
          <div className="gold-line center reveal delay-2" />
          <div style={{ display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:'48px',marginTop:'56px' }}>
            {p.faces.map((face, i) => (
              <div key={i} className="reveal" style={{ transitionDelay:`${i*0.15}s`,textAlign:'center' }}>
                <div style={{ width:'160px',height:'160px',margin:'0 auto 20px',borderRadius:'50%',overflow:'hidden',border:'2px solid rgba(200,150,60,0.2)' }}>
                  <img src={`${pub}/${face.image}`} alt={face.name} style={{ width:'100%',height:'100%',objectFit:'cover' }} />
                </div>
                <h4 style={{ fontFamily:'var(--font-title)',fontSize:'1.2rem',color:'var(--text)',marginBottom:'6px' }}>{face.name}</h4>
                <span style={{ fontFamily:'var(--font-body)',fontSize:'10px',letterSpacing:'0.2em',textTransform:'uppercase',color:'var(--gold)',display:'block',marginBottom:'12px' }}>{face.role}</span>
                <div style={{ width:'24px',height:'1px',background:'var(--gold)',margin:'0 auto 12px' }} />
                <p style={{ fontFamily:'var(--font-body)',fontSize:'12px',color:'var(--text2)',lineHeight:1.7 }}>{face.bio}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Discover SKA */}
      <div style={{ position:'relative',overflow:'hidden' }}>
        <img src={`${pub}/${p.discoverSkaImage}`} alt="Discover SKA" style={{ width:'100%',height:'580px',objectFit:'cover',display:'block' }} />
        <div style={{ position:'absolute',inset:0,background:'rgba(14,12,10,0.55)',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',textAlign:'center',padding:'0 48px' }}>
          <h2 style={{ fontFamily:'var(--font-title)',fontWeight:400,fontSize:'clamp(2rem,4.5vw,3.8rem)',color:'white',lineHeight:1.1,marginBottom:'20px' }}>Discover SKA</h2>
          <p style={{ fontFamily:'var(--font-body)',fontSize:'13px',lineHeight:1.8,color:'rgba(255,255,255,0.7)',maxWidth:'680px',marginBottom:'52px' }}>
            Embark on a Fascinating Exploration of the Statistical Landscape That Shapes Our Identity, Unveiling Our Rich Heritage, Accomplishments, and Unwavering Dedication to Providing Unforgettable Experiences for Every Guest Who Crosses Our Threshold.
          </p>

          {/* Counter row */}
          <CounterRow />

          <a href="/#rooms" style={{ fontFamily:'var(--font-body)',fontSize:'11px',letterSpacing:'0.2em',textTransform:'uppercase',color:'white',border:'1px solid rgba(255,255,255,0.6)',padding:'14px 36px',transition:'all 0.25s' }}
            onMouseEnter={e=>{e.target.style.background='white';e.target.style.color='var(--dark)';e.target.style.borderColor='white';}}
            onMouseLeave={e=>{e.target.style.background='transparent';e.target.style.color='white';e.target.style.borderColor='rgba(255,255,255,0.6)';}}
          >Explore Rooms</a>
        </div>
      </div>

    </div>
  );
}
